import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete, Box, Button, CircularProgress, Grid, MenuItem, Stack, TextField, Typography,
  Divider, Chip,
} from "@mui/material";
import { toast } from "react-toastify";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import SearchIcon from "@mui/icons-material/Search";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { PageHeader, SectionCard, EmptyState } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";
import { sortByLabel } from "../../utils/sortOptions";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const money = (n) => `₹${(parseFloat(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function SalarySlipFormNew() {
  const [employees, setEmployees] = useState([]);
  const [emp, setEmp] = useState(null);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [slip, setSlip] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(endpoints.employees.list)
      .then((r) => setEmployees(Array.isArray(r.data) ? r.data : r.data?.data || []))
      .catch(() => setEmployees([]));
  }, []);

  const opts = useMemo(
    () => sortByLabel(employees.map((e) => ({ id: e.employeeid, label: `${e.name} (${e.employeeid})`, meta: e })), (o) => o.label),
    [employees]
  );
  const years = useMemo(() => Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i), []);

  const generate = async () => {
    if (!emp) return toast.error("Select an employee");
    setLoading(true);
    setSlip(null);
    try {
      const res = await api.post("/api/salaryslip/generate", { employeeid: emp.id, month, year });
      setSlip(res.data);
      toast.success("Slip generated");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  const downloadPdf = async () => {
    if (!slip) return;
    try {
      const jsPDF = (await import("jspdf")).default;
      await import("jspdf-autotable");
      const doc = new jsPDF();
      doc.setFontSize(16); doc.text("Salary Slip", 14, 20);
      doc.setFontSize(10);
      doc.text(`Employee: ${slip.name || emp.meta.name}`, 14, 30);
      doc.text(`ID: ${slip.employeeid || emp.id}`, 14, 36);
      doc.text(`Period: ${MONTHS[month - 1]} ${year}`, 14, 42);
      doc.autoTable({
        startY: 50,
        head: [["Earnings", "Amount", "Deductions", "Amount"]],
        body: [
          ["Basic", money(slip.basic || 0), "PF", money(slip.pf || 0)],
          ["HRA", money(slip.hra || 0), "ESI", money(slip.esi || 0)],
          ["Special Allowance", money(slip.specialAllowance || 0), "PT", money(slip.pt || 0)],
          ["Overtime", money(slip.overtimePay || 0), "TDS", money(slip.tds || 0)],
        ],
      });
      doc.setFontSize(12);
      doc.text(`Net Pay: ${money(slip.netPay || 0)}`, 14, doc.lastAutoTable.finalY + 15);
      doc.save(`salary-slip-${emp.id}-${MONTHS[month - 1]}-${year}.pdf`);
    } catch {
      toast.error("PDF export failed - jspdf not installed");
    }
  };

  return (
    <Box p={3}>
      <PageHeader
        title="Generate Salary Slip"
        subtitle="Pick an employee and pay period"
        icon={<ReceiptOutlinedIcon />}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={slip ? 4 : 12}>
          <SectionCard title="Parameters" accent="#6C50BF">
            <Stack spacing={2}>
              <Autocomplete
                options={opts} value={emp} onChange={(_, v) => setEmp(v)}
                getOptionLabel={(o) => o?.label || ""}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(p) => <TextField {...p} label="Employee *" />}
              />
              <Stack direction="row" spacing={1.5}>
                <TextField select fullWidth label="Month" value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))}>
                  {/* Not sorted: the MenuItem value is derived from the index (i + 1 = month number), so reordering would break the value mapping. */}
                  {MONTHS.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
                </TextField>
                <TextField select fullWidth label="Year" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))}>
                  {sortByLabel(years).map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
              </Stack>
              <Button
                variant="contained" size="large" startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
                onClick={generate} disabled={loading || !emp}
              >
                {loading ? "Generating…" : "Generate Slip"}
              </Button>
            </Stack>
          </SectionCard>
        </Grid>

        {slip && (
          <Grid item xs={12} md={8}>
            <SectionCard
              title={`${slip.name || emp.meta.name} • ${MONTHS[month - 1]} ${year}`}
              subtitle={`Employee ID: ${slip.employeeid || emp.id}`}
              accent="#39C645"
              actions={[
                <Button key="pdf" variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={downloadPdf}>
                  Download PDF
                </Button>,
              ]}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#39C645", textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>Earnings</Typography>
                  <Stack spacing={1} sx={{ p: 2, borderRadius: 2, bgcolor: "#EDFAF2" }}>
                    {[
                      ["Basic", slip.basic],
                      ["HRA", slip.hra],
                      ["Special Allowance", slip.specialAllowance],
                      ["Overtime", slip.overtimePay],
                      ["Bonus", slip.bonus],
                    ].filter(([, v]) => v).map(([l, v]) => (
                      <Stack key={l} direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: 13 }}>{l}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{money(v)}</Typography>
                      </Stack>
                    ))}
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Gross</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#39C645" }}>{money(slip.grossPay || 0)}</Typography>
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#FF6B6B", textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>Deductions</Typography>
                  <Stack spacing={1} sx={{ p: 2, borderRadius: 2, bgcolor: "#FAEDED" }}>
                    {[
                      ["PF", slip.pf],
                      ["ESI", slip.esi],
                      ["Professional Tax", slip.pt],
                      ["TDS", slip.tds],
                      ["Loan", slip.loanDeductions],
                      ["LWF", slip.lwf],
                    ].filter(([, v]) => v).map(([l, v]) => (
                      <Stack key={l} direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: 13 }}>{l}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{money(v)}</Typography>
                      </Stack>
                    ))}
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Total</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#FF6B6B" }}>{money(slip.totalDeductions || 0)}</Typography>
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{ p: 3, borderRadius: 3, background: "linear-gradient(135deg, #6C50BF 0%, #8568D4 100%)", color: "#fff", textAlign: "center" }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.85 }}>Net Pay</Typography>
                    <Typography sx={{ fontSize: 36, fontWeight: 800 }}>{money(slip.netPay || 0)}</Typography>
                    {slip.presentDays != null && (
                      <Stack direction="row" justifyContent="center" spacing={2} mt={1}>
                        <Chip label={`Present: ${slip.presentDays}`} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff" }} />
                        {slip.leaveDays != null && <Chip label={`Leaves: ${slip.leaveDays}`} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff" }} />}
                      </Stack>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
